import { Check, type IconProps } from '@phosphor-icons/react';
import { cn } from '@skupreme/shared/ui';
import {
  createElement,
  forwardRef,
  useCallback,
  type HTMLAttributes,
} from 'react';

type DivProps = HTMLAttributes<HTMLDivElement>;

function div(displayName: string, base: string) {
  const Component = forwardRef<HTMLDivElement, DivProps>(
    ({ className, ...rest }, ref) =>
      createElement('div', { ...rest, ref, className: cn(base, className) }),
  );
  Component.displayName = displayName;
  return Component;
}

/* Slides off the check mark it covers, so the mark appears to be drawn. */
const REVEAL: Keyframe[] = [
  { left: '43%', width: '100px', offset: 0 },
  { left: '100%', width: '100px', offset: 0.9 },
  { left: '100%', width: '1px', offset: 1 },
];

export const WhiteOverlay = forwardRef<HTMLDivElement, DivProps>(
  ({ className, ...rest }, ref) => {
    const attach = useCallback(
      (node: HTMLDivElement | null) => {
        // jsdom has no Web Animations API.
        if (node && typeof node.animate === 'function') {
          node.animate(REVEAL, { duration: 2000, fill: 'forwards' });
        }
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref],
    );
    return createElement('div', {
      ...rest,
      ref: attach,
      className: cn(
        'sk:absolute sk:top-1/2 sk:left-[43%] sk:size-[100px] sk:-translate-y-1/2 sk:bg-surface',
        className,
      ),
    });
  },
);
WhiteOverlay.displayName = 'WhiteOverlay';

export const Steps = div(
  'Steps',
  cn(
    'sk:relative sk:flex sk:flex-wrap sk:items-center sk:gap-x-2.5 sk:gap-y-1.5',
    'sk:border-b sk:border-solid sk:border-border-subtle sk:px-4 sk:py-2.5',
    'sk:[&_.icon]:min-w-[15px]',
  ),
);

type StepProps = DivProps & { allowed?: boolean | number; active?: number };

export const Step = forwardRef<HTMLDivElement, StepProps>(
  ({ allowed, active: _active, className, ...rest }, ref) =>
    createElement('div', {
      ...rest,
      ref,
      className: cn(
        'sk:flex sk:items-center sk:gap-x-1.5',
        allowed ? 'sk:cursor-pointer' : 'sk:cursor-not-allowed',
        'sk:[&_.active]:font-bold sk:[&.single-stepper]:cursor-auto',
        className,
      ),
    }),
);
Step.displayName = 'Step';

export const ActiveStep = div('ActiveStep', 'sk:pt-4 sk:pb-2.5');

export function CheckMark({ className, ...rest }: IconProps) {
  return createElement(Check, {
    ...rest,
    className: cn('sk:text-success sk:opacity-70', className),
  });
}

export const AnimateCheck = div(
  'AnimateCheck',
  'sk:absolute sk:inset-0 sk:z-[1100] sk:flex sk:items-center sk:justify-center sk:bg-surface sk:text-success',
);
